import { useState, useMemo, type JSX } from "react";
import yaml from "js-yaml";
import YamlMetadataPanel from "./components/yamlPreview/YamlMetadataPanel";
import YamlParagraphsPanel from "./components/yamlPreview/YamlParagraphsPanel";
import { YamlParseError, YamlRawFallback } from "./components/yamlPreview/YamlPreviewStates";
import type { YamlDocumentData } from "./components/yamlPreview/yamlPreviewTypes";

interface YamlDocumentPreviewProps {
  contentText: string;
}

interface ParsedYaml {
  parsedData: YamlDocumentData | null;
  parseError: string | null;
}

/** Which paragraphs are open, plus the expand/collapse controls. */
function useExpandedParagraphs(parsedData: YamlDocumentData | null): {
  expandedParagraphs: Set<number>;
  toggleParagraph: (index: number) => void;
  toggleAll: (expand: boolean) => void;
} {
  const [expandedParagraphs, setExpandedParagraphs] = useState<Set<number>>(new Set());

  const toggleParagraph = (index: number): void => {
    const newExpanded = new Set(expandedParagraphs);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedParagraphs(newExpanded);
  };

  const toggleAll = (expand: boolean): void => {
    if (expand && parsedData?.paragraphs) {
      setExpandedParagraphs(new Set(parsedData.paragraphs.map((_, idx) => idx)));
    } else {
      setExpandedParagraphs(new Set());
    }
  };

  return { expandedParagraphs, toggleParagraph, toggleAll };
}

/**
 * YamlDocumentPreview Component
 * Renders structured YAML documents with collapsible sections
 * Renders arbitrary YAML documents as collapsible key/value sections
 */
export default function YamlDocumentPreview_Component({
  contentText,
}: YamlDocumentPreviewProps): JSX.Element {
  const [metadataExpanded, setMetadataExpanded] = useState<boolean>(true);

  // Parse YAML and handle errors
  const { parsedData, parseError } = useMemo<ParsedYaml>(() => {
    try {
      const data = yaml.load(contentText) as YamlDocumentData;
      return { parsedData: data, parseError: null };
    } catch (error) {
      return { parsedData: null, parseError: (error as Error).message };
    }
  }, [contentText]);

  const { expandedParagraphs, toggleParagraph, toggleAll } = useExpandedParagraphs(parsedData);

  if (parseError) {
    return <YamlParseError message={parseError} />;
  }

  // Check if this YAML has the paragraphs structure we can preview
  if (!parsedData || !Array.isArray(parsedData.paragraphs)) {
    return <YamlRawFallback contentText={contentText} />;
  }

  // Render structured document with collapsible sections
  return (
    <div className="h-full overflow-auto bg-gray-50">
      <div className="max-w-5xl mx-auto py-6 px-4">
        {parsedData.metadata && (
          <YamlMetadataPanel
            metadata={parsedData.metadata}
            expanded={metadataExpanded}
            onToggle={() => setMetadataExpanded(!metadataExpanded)}
          />
        )}
        <YamlParagraphsPanel
          paragraphs={parsedData.paragraphs}
          expandedParagraphs={expandedParagraphs}
          onToggleParagraph={toggleParagraph}
          onToggleAll={toggleAll}
        />
      </div>
    </div>
  );
}
